import {
  isMobileDrawerQueryEnabled,
  parseDrawerQuery,
} from '~/composables/useDrawerQueryNavigation'

const PREVENT_SCROLL_CLASS = 'prevent-scroll'

/**
 * Toggle the global body scroll lock used by drawers/sidebars.
 * Kept as a standalone helper so it can be shared between `useUiState`
 * (which locks when a drawer opens) and the app-level route reconciler
 * (which guarantees the lock is released on navigation).
 */
export const setBodyScrollLocked = (locked: boolean) => {
  if (typeof document === 'undefined' || !document.body) return

  document.body.classList.remove(PREVENT_SCROLL_CLASS)
  if (locked) {
    document.body.classList.add(PREVENT_SCROLL_CLASS)
  }
}

/**
 * Reconcile the body scroll lock against the current route on every
 * navigation. Mounted once at the app root (`app.vue`), which never
 * unmounts, so the lock is reliably released even when navigating to
 * pages that don't mount the drawer components (e.g. the `blank`-layout
 * checkout page). Without this, a scroll lock applied by an open mobile
 * drawer could persist onto the checkout page and get stuck, because the
 * only cleanup lived inside `useUiState` — which the checkout page never
 * instantiates.
 *
 * The drawer query only opens a drawer on mobile (see
 * `isMobileDrawerQueryEnabled`); on desktop the query string can still be
 * present (typed, pasted, shared) without any drawer ever rendering, so the
 * lock must check the same mobile gate `useUiState` uses — otherwise scroll
 * gets locked on desktop with no visible drawer to blame it on.
 */
export const useBodyScrollLockRouteSync = () => {
  if (!import.meta.client) return

  const route = useRoute()

  watch(
    () => [route.path, parseDrawerQuery(route.query)] as const,
    ([, drawer]) => {
      setBodyScrollLocked(isMobileDrawerQueryEnabled() && !!drawer)
    },
    { immediate: true }
  )
}
